import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import {
  sendGlobalLayoutProps,
  sendGlobalErrorProps,
  waitForElement,
  ENVIRONMENTS,
  MasterLayoutProps,
  parseJwt,
} from 'cu-master-layout-prop-manager';
import { hasAuthParams, useAuth as useOidcAuth } from 'react-oidc-context';
import { useLocation } from 'react-router';

import { apiServices } from '@/api';
import { APP_ROUTES } from '@/routes';
import { Nullable } from '@/types';

const env = ENVIRONMENTS[import.meta.env.VITE_ENVIRONMENT_ID];

interface OidcInnerError {
  error?: string;
  error_description?: Nullable<string>;
  error_uri?: Nullable<string>;
  session_state?: string;
}

export function useAuth() {
  const [hasTriedSignin, setHasTriedSignin] = useState(false);
  const auth = useOidcAuth();
  const location = useLocation();
  const token = auth.user?.access_token;
  const fullName = token ? parseJwt(token)?.name : '';
  const globalProps = useMemo(
    () => ({
      token,
      env,
      fullName,
      activeLink: location.pathname === APP_ROUTES.aires ? APP_ROUTES.aires : APP_ROUTES.loansMapping,
    }),
    [token, fullName, location],
  ) as MasterLayoutProps;

  useEffect(() => {
    if (!hasAuthParams() && !auth.isAuthenticated && !auth.activeNavigator && !auth.isLoading && !hasTriedSignin) {
      void auth.signinRedirect();
      setHasTriedSignin(true);
      sendGlobalLayoutProps(globalProps);
    }
  }, [auth, hasTriedSignin, globalProps]);

  useEffect(() => {
    if (!hasAuthParams() && auth.isAuthenticated && auth.user?.expired) {
      auth.signinSilent().catch(() => {
        void auth.signinRedirect();
      });
    }
  }, [auth, auth.user?.expired]);

  useLayoutEffect(() => {
    const services = Object.values(apiServices);

    const interceptors = services.map((service) =>
      service.interceptors.request.use((config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        return config;
      }),
    );

    return () => {
      services.forEach((service, index) => {
        service.interceptors.request.eject(interceptors[index]);
      });
    };
  }, [token]);

  if (auth.isLoading) {
    return auth;
  }

  if (auth.error) {
    console.error('Auth error. Please check the defined auth credentials', auth.error);
    sendGlobalErrorProps({ errorType: 'generalError' });

    if (
      auth.error.message === 'login_required' ||
      (auth.error.innerError && (auth.error.innerError as OidcInnerError).error === 'login_required')
    ) {
      void auth.signinRedirect();
      return auth;
    }
  }

  if (!auth.isAuthenticated) {
    return auth;
  }

  if (auth.isAuthenticated && token) {
    waitForElement('#master_layout_logout_btn')
      .then((element) => {
        element.removeAttribute('href');
        element.addEventListener('click', () => {
          void auth.signoutRedirect({
            id_token_hint: auth.user?.id_token,
          });
        });
      })
      .catch(console.error);

    sendGlobalLayoutProps(globalProps);
  }

  return auth;
}
